import React, { useState, useEffect } from 'react';
import { getFirestore, collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import toast from 'react-hot-toast';

const UserRow = ({ user }) => (
  <div className="grid grid-cols-3 gap-4 p-4 border-b hover:bg-gray-50 transition-colors items-center">
    <div className="flex flex-col">
      <span className="font-semibold text-slate-800">{user.displayName || "Anonymous User"}</span>
      <span className="text-xs text-gray-400 font-mono">{user.id}</span>
    </div>
    <span className="text-gray-600">{user.email}</span>
    <span className="text-gray-500 text-sm text-right">
      Joined: {user.createdAt 
        ? (user.createdAt.toDate ? user.createdAt.toDate().toLocaleDateString() : new Date(user.createdAt).toLocaleDateString())
        : 'N/A'}
    </span>
  </div>
);

const ArtistRow = ({ artist, onDeleteClick }) => (
  <div className={`grid grid-cols-3 gap-4 p-4 border-b hover:bg-gray-50 transition-colors items-center ${artist.isDeleted ? 'bg-red-50 opacity-70' : ''}`}>
    <div className="flex flex-col">
      <div className="flex items-center gap-2">
        <span className="font-bold text-blue-900">{artist.artistName}</span>
        {artist.isDeleted && <span className="text-[10px] bg-red-200 text-red-700 px-2 py-0.5 rounded-full uppercase font-black">Deleted</span>}
      </div>
      <span className="text-xs text-gray-400 font-mono">{artist.id}</span>
    </div>
    <div className="flex flex-col">
       <span className="text-gray-600">{artist.email}</span>
       <span className="text-xs text-gray-400">{artist.city}, {artist.state}</span>
    </div>
    <div className="flex items-center justify-end gap-4">
      {/* ✅ Delete Button (Only shows if not already deleted) */}
      {!artist.isDeleted && (
        <button 
          onClick={() => onDeleteClick(artist)}
          className="text-xs font-bold text-red-500 hover:text-red-700 transition-colors border border-red-200 hover:border-red-500 px-3 py-1 rounded"
        >
          DELETE
        </button>
      )}
      <span className="text-gray-500 text-sm text-right">
        {artist.createdAt ? new Date(artist.createdAt).toLocaleDateString() : 'N/A'}
      </span>
    </div>
  </div>
);

const UsersTab = () => {
  const [subTab, setSubTab] = useState('users');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmingDelete, setConfirmingDelete] = useState(null); // Holds the artist object to delete
  const [isDeleting, setIsDeleting] = useState(false);
  const db = getFirestore();
  const functions = getFunctions(undefined, 'us-central1');

  useEffect(() => {
    setLoading(true);
    const collectionName = subTab === 'users' ? 'users' : 'artist_accounts';
    const q = query(collection(db, collectionName), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setData(items);
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [subTab, db]);

  const handlePermanentDelete = async () => {
    if (!confirmingDelete) return;
    setIsDeleting(true);

    try {
      const deleteArtistAccount = httpsCallable(functions, 'deleteArtistAccount');
      await deleteArtistAccount({ artistId: confirmingDelete.id });
      
      toast.success(`${confirmingDelete.artistName} has been flagged for deletion.`);
      setConfirmingDelete(null);
    } catch (error) {
      console.error("Delete Error:", error);
      toast.error("Failed to delete account. Check console for details.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="p-6">
      {/* --- Confirmation Modal --- */}
      {confirmingDelete && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-2xl">
            <h3 className="text-lg font-bold text-slate-800">Delete Artist Account?</h3>
            <p className="text-sm text-gray-500 mt-2">
              Are you sure you want to delete <span className="font-bold text-slate-800">{confirmingDelete.artistName}</span>? 
              This will hide their music and profile for 90 days before permanent purging.
            </p>
            <div className="flex gap-3 mt-6">
              <button 
                disabled={isDeleting}
                onClick={() => setConfirmingDelete(null)}
                className="flex-1 px-4 py-2 text-sm font-bold text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button 
                disabled={isDeleting}
                onClick={handlePermanentDelete}
                className="flex-1 px-4 py-2 text-sm font-bold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-lg shadow-red-200"
              >
                {isDeleting ? "Deleting..." : "Confirm Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex gap-x-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
        <button 
          onClick={() => setSubTab('users')} 
          className={`px-6 py-2 rounded-md text-sm font-bold transition-all ${subTab === 'users' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
        >
          General Users
        </button>
        <button 
          onClick={() => setSubTab('artists')} 
          className={`px-6 py-2 rounded-md text-sm font-bold transition-all ${subTab === 'artists' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
        >
          Artist Accounts
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 border-b text-xs font-black uppercase tracking-widest text-gray-400">
          <span>Name / ID</span>
          <span>Contact Info</span>
          <span className="text-right">Registration Date</span>
        </div>
        
        {loading ? (
          <div className="p-10 text-center text-gray-400">Loading directory...</div>
        ) : data.length === 0 ? (
          <div className="p-10 text-center text-gray-400">No records found.</div>
        ) : (
          data.map(item => subTab === 'users' 
            ? <UserRow key={item.id} user={item} /> 
            : <ArtistRow 
                key={item.id} 
                artist={item} 
                onDeleteClick={(artist) => setConfirmingDelete(artist)} // ✅ PASS THIS PROP
              />
          )
        )}
      </div>
    </div>
  );
};

export default UsersTab;